import { Camera, type GalleryPhoto } from "@capacitor/camera";
import imageCompression from "browser-image-compression";
import { useState } from "react";

import { uint8ToDataUrl } from "@/lib/image-source.ts";
import { logError } from "@/lib/utils/log-error.ts";

export interface FileObjectType {
  file: Uint8Array | null;
  mimetype: string;
}

export const useFileSelector = ({
  onError,
  onSelectFile,
  onUploadFile,
  allowPdf = false,
}: {
  onError?: () => void;
  onSelectFile?: () => Promise<void>;
  onUploadFile?: (file: FileObjectType) => Promise<void>;
  allowPdf?: boolean;
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const [fileObject, setFileObject] = useState<FileObjectType>({
    file: null,
    mimetype: "",
  });

  const openFileSelector = async (): Promise<void> => {
    setFileObject({ file: null, mimetype: "" });
    setError(undefined);
    setIsUploading(false);

    if (allowPdf) {
      openNativeFilePicker();
      return;
    }

    try {
      const hasPermission = await checkAndRequestPermissions();

      if (!hasPermission) return;

      const firstSelectedPhoto = await pickImage();
      if (!firstSelectedPhoto) return;

      setIsUploading(true);
      await onSelectFile?.();
      const compressedFile = await fetchAndCompressImage(firstSelectedPhoto.webPath);

      readFileData(compressedFile, firstSelectedPhoto.format);
      setIsUploading(false);
    } catch (exceptionError) {
      setError("Erro ao carregar a imagem.");
      onError?.();
      logError(exceptionError);
    }
  };

  const processSelectedFile = async (file: File): Promise<void> => {
    try {
      const mimetype = file.type;
      const processedFile = mimetype.startsWith("image/")
        ? await compressImage(file)
        : file;

      const buffer = await processedFile.arrayBuffer();
      const arrayDeBytes = new Uint8Array(buffer);
      setFileObject({ file: arrayDeBytes, mimetype });
      if (onUploadFile) {
        void onUploadFile({ file: arrayDeBytes, mimetype });
      }
    } catch (err: unknown) {
      setError("Erro ao carregar o arquivo.");
      onError?.();
      logError(err);
    } finally {
      setIsUploading(false);
    }
  };

  const openNativeFilePicker = (): void => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,application/pdf";
    input.onchange = (event): void => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;

      setIsUploading(true);
      void processSelectedFile(file);
    };
    input.click();
  };

  const checkAndRequestPermissions = async (): Promise<boolean> => {
    let permission = await Camera.checkPermissions();
    if (permission.photos === "denied") {
      permission = await Camera.requestPermissions();
    }

    if (permission.photos === "denied") {
      setError("Permissão negada para acessar as fotos.");
      onError?.();
      return false;
    }

    return true;
  };

  const pickImage = async (): Promise<GalleryPhoto | null> => {
    const result = await Camera.pickImages({ limit: 1 });

    return result.photos.at(0) ?? null;
  };

  const compressImage = async (file: File): Promise<File> => {
    return await imageCompression(file, {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    });
  };

  const fetchAndCompressImage = async (webPath: string): Promise<File> => {
    const response = await fetch(webPath);
    const responseBlob = await response.blob();
    const fileFromBlob = new File([responseBlob], "profile_picture", {
      type: responseBlob.type,
    });

    return await compressImage(fileFromBlob);
  };

  const readFileData = (file: File, format: string): void => {
    const reader = new FileReader();
    reader.onload = (): void => {
      const arrayDeBytes = new Uint8Array(reader.result as ArrayBuffer);
      const mimetype = `image/${format}`;
      setFileObject({ file: arrayDeBytes, mimetype });
      if (onUploadFile) {
        void onUploadFile({ file: arrayDeBytes, mimetype });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const dataUrl =
    fileObject.file && fileObject.mimetype
      ? uint8ToDataUrl(fileObject.file, fileObject.mimetype)
      : null;

  return {
    fileData: fileObject.file,
    mimetype: fileObject.mimetype,
    dataUrl,
    isUploading,
    error,
    openFileSelector,
  };
};
